import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getBlockDetails,
  getBlockDimensions,
  getIndicatorTableData,
  resetMultiDimFilter,
  setFilterItems,
  setResetFilter,
  UpdatePlangPageIndicatorTableData,
} from 'redux/PlanPageSlice';
import FigmaDimensionsIcon from 'components/Icons/FigmaDimensionsIcon';
import { getBaseScenario } from 'redux/ScenarioSlice';
import {
  PLAN_REVAMP_RADIUS_SM,
  accent,
  controlHeight,
  fontSize,
  neutral,
  text as textToken,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';

interface Properties {
  refetchRequest: () => void;
  indicatorId: any;
}

const MultiDimSetting = ({ indicatorId, refetchRequest }: Properties) => {
  const dispatch: any = useDispatch();
  const indicatorTableData = useSelector(getIndicatorTableData);
  const baseScenario = useSelector(getBaseScenario);

  const dimensionList = useSelector(getBlockDetails);
  const dimensionItemList = useSelector(getBlockDimensions);
  const [dimensionData, setDimensionData] = useState(dimensionList?.dimensions || []);
  const [activeDim, setActiveDim] = useState<any>();

  useEffect(() => {
    const dimensionsList = dimensionList?.dimensions;
    setDimensionData(dimensionsList);
  }, [dimensionList?.dimensions]);
  useEffect(() => {
    const active = indicatorTableData?.dimensions?.length
      ? Number(indicatorTableData?.dimensions?.length) + 1
      : 1;
    setActiveDim(active);
  }, [indicatorTableData]);

  const doesDimensionExist = (dim: any, dimArray: any[]) => {
    if (dim?.name?.toLowerCase() === 'time') return true;
    const exists = dimArray?.some((data) => Number(data?.id) === dim?.id);
    return exists;
  };
  function addDim(currentDim: any, newDim: any) {
    let found = false;
    const updatedDim = JSON.parse(JSON.stringify(currentDim));
    updatedDim.forEach((dim: any, index: any) => {
      if (dim.id === String(newDim.id)) {
        updatedDim.splice(index, 1);
        found = true;
      }
    });
    if (!found) {
      updatedDim.push({ id: String(newDim.id) });
    }
    return updatedDim;
  }

  const handleSettingChange = async (data: any) => {
    const dim = indicatorTableData?.dimensions;
    const result = await addDim(dim, data);
    const payload = {
      indicator_id: indicatorId,
      request_body: {
        dimensions: result,
        bulk_input: true,
        type: indicatorTableData?.type,
        format_excel_data: true,
        scenario_id: baseScenario?.id,
      },
    };
    await dispatch(resetMultiDimFilter());
    await dispatch(setFilterItems([]));
    await dispatch(setResetFilter(true));
    await dispatch(UpdatePlangPageIndicatorTableData(payload));
    await refetchRequest();
  };

  const hasDimensionItems = (id: number) => {
    const dimension = dimensionItemList?.dimensions?.find((dim: any) => dim.id === id);

    if (dimension?.items.length > 0) {
      return true;
    }
    return false;
  };

  const renderMenuList = (list: any) => {
    const filteredList: any = [...(list || [])];
    return (
      <Box maxH='190px' overflowY='auto'>
        {filteredList?.map((data: any, index: number) => (
          <Flex key={index} alignItems='center' py='6px' gap={2}>
            <Switch
              size='sm'
              colorScheme='purple'
              isDisabled={
                data?.name === 'time' || data?.name === 'Time' || !hasDimensionItems(data?.id)
              }
              isChecked={doesDimensionExist(data, indicatorTableData?.dimensions)}
              onChange={() => handleSettingChange(data)}
            />
            {!hasDimensionItems(data?.id) && data?.name !== 'time' && data?.name !== 'Time' ? (
              <Tooltip label='Dimension has no items'>
                <Text fontSize={fontSize.body} color={textToken.secondary}>
                  {data?.name}
                </Text>
              </Tooltip>
            ) : (
              <Text fontSize={fontSize.body} color={textToken.primary}>
                {data?.name}
              </Text>
            )}
          </Flex>
        ))}
      </Box>
    );
  };

  return (
    <Popover closeOnBlur placement='bottom-end'>
      <PopoverTrigger>
        <Flex
          as='button'
          type='button'
          alignItems='center'
          gap='6px'
          h={controlHeight}
          px='10px'
          bg={neutral.surface}
          border='1px solid'
          borderColor={neutral.border}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          cursor='pointer'
          transition='background 0.12s ease, border-color 0.12s ease'
          _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
        >
          <Tooltip label='Block Dimensions'>
            <Flex alignItems='center' gap='6px'>
              <FigmaDimensionsIcon width={14} height={14} color={accent.solid} />
              <Text fontSize={fontSize.body} fontWeight={500} color={textToken.primary}>
                {activeDim} / {dimensionData?.length || 0}
              </Text>
              <ChevronDownIcon boxSize={4} color={textToken.secondary} />
            </Flex>
          </Tooltip>
        </Flex>
      </PopoverTrigger>
      <PopoverContent
        bg={neutral.surface}
        border='1px solid'
        borderColor={neutral.border}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        boxShadow='md'
        maxH='250px'
        w='230px'
      >
        <Box px={3} py={2}>
          {renderMenuList(dimensionData)}
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default MultiDimSetting;
